
import { Component, Vue, Prop, Watch, Ref } from "vue-property-decorator"
import { Getter, State, namespace } from "vuex-class"

import { CalculationType, CaseDetails, DataType, User, CaseField, Permission, Status, Severity, TemplateDetails } from "@/types"
import { calculatedValueDisplay } from "@/mixins/calculatedValueDisplay"

import { cloneDeep, get } from "lodash"
import UserImage from "@/components/UserImage.vue"
import DateTime from "../Fields/DateTime.vue"
import Numeric from "../Fields/Numeric.vue"

import api from "@/services/api"

import vSelect from "vue-select"
import { FormattingRule, editableFieldTypes, Insight } from "@/types/insights"
import { CaseColumn, isBuiltInCaseColumn, getBuiltInCaseColumn } from "@/types/caseColumns"
import { cellFormatting } from "@/mixins/cellFormatting"
import { UserSettings, ViewsOpeningOption } from "@/types/userSettings"
import TimeSpentDisplay from "../CaseDetails/TimeSpentDisplay.vue"
import TextAreaToggleExpand from "../Fields/TextAreaToggleExpand.vue"
import UserRole from "../CaseDetails/Users/UserRole.vue"
import FormGroup from "../Fields/FormGroup.vue"
import DataSourceSelect from "../Fields/DataSourceSelect.vue"
import { TenantFeatures } from "@/types/tenantFeatures"
import { LocationLabels } from "@/types/location"
import { formatFieldsMixin } from "@/mixins/formatFieldsMixin"
import { TenantSettingKeys } from "@/types/tenantSetting"
import { View } from "@/types/view"
import { inlineEditMode } from "@/types/insights/selectiveInlineEdit"
import Tags from "../Fields/Tags.vue"
import { getCanUserDisableCadence, normalizeCaseCadenceProperties } from "@/scripts/caseDetails"
import { CurrentUser } from "@/types/user"
import AreaSelect from "../Fields/AreaSelect.vue"
import { FieldTypeId } from "@/types/fieldTypes"
import DataDetailsModal from "../CaseDetails/DataDetailsModal.vue"
import DataSourceTemplate from "../Fields/DataSourceTemplate.vue"
import { cellValue, DataObjectMethods } from "@/scripts/caseFields"
import DataCache from "@/caching/caching"
import { getHrefForExternalLink } from "@/utils/externalLinkUtil"

const caseStore = namespace("cases")

@Component({
	components: {
		"v-select": vSelect,
		"user-image": UserImage,
		"time-spent-display": TimeSpentDisplay,
		"text-area-expand": TextAreaToggleExpand,
		"date-time": DateTime,
		"numeric": Numeric,
		"user-role": UserRole,
		"form-group": FormGroup,
		"data-select": DataSourceSelect,
		"tags": Tags,
		"area-select": AreaSelect,
		"data-template": DataSourceTemplate,
		"data-details": DataDetailsModal
	}
})
export default class CaseTable extends Vue {
	@Ref() dataDetailsModal!: DataDetailsModal;

	@Getter("getMe") public me!: CurrentUser;
	@Getter("getUserSettings")
	public userSettings!: UserSettings;
	@Getter("getTenantSettings") getTenantSettings!: { [ key: string]: any }
	@Getter getIsFeatureEnabled!: (feature: TenantFeatures) => boolean
	@Getter("getCurrentRouteName") public currentRouteName!: string;
	@Getter getCaseName!: string;
	@State private readonly templateCache!: DataCache<TemplateDetails>
	@State tenantEnabledFeatures!: { [key: string]: boolean }

	@caseStore.Getter("getStatuses") private readonly statuses!: Status[];
	@caseStore.Getter("getSeverities") private readonly severities!: Severity[];

	@Prop({ default: false })
	showSelectColumn!:boolean

	@Prop({ default: () => null })
	view!: View | null;

	@Prop()
	columns!: CaseColumn[];

	@Prop({ default: null })
	caseIdToEdit!: string | null

	@Prop()
	checkedItems!: string[]

	@Prop({ default: () => { return {} } })
	formattingRules!: { [key: string]: FormattingRule[] }

	@Prop()
	cases!: CaseDetails[];

	@Prop({ default: () => null })
	currentPage!: number | null;

	@Prop({ default: true })
	hover!: boolean;

	@Prop({ default: 50 })
	perPage!: number;

	@Prop({ default: () => [] })
	filterFunctions!: ((record: CaseDetails) => boolean)[];

	@Prop({ default: false })
	public displayingUsers!: boolean;

	@Prop({ default: false })
	showFilters!: boolean

	@Prop({ default: () => ({}) })
	checkStyleFilterSelections!: { [columnKey: string]: any[] }

	@Prop({ type: Boolean, default: false })
	editModeEnabled!: boolean

	@Prop({ type: Array, default: null })
	columnHeadersToHighlight!: string[]

	@Prop({ type: Object, default: null })
	updatedCase!: CaseDetails | null

	@Prop({ type: Number, default: null })
	updatedCaseKey!: number | null

	@Prop({ type: Boolean, default: false })
	isFirstGroup!: boolean

	@Prop({ type: Boolean, default: false })
	disableOldInlineEdit!: boolean

	@Prop({ type: Object, default: () => ({}) })
	cellErrors!: { [caseId: string]: { columnKeys: string[] } }

	TenantFeatures = TenantFeatures

	reactivityHook: number = 0
	rowClickRequestToken: number = 0
	showTimeAsHoursMinutes: boolean = true
	sortColumn: string | null = null
	sortAscending: boolean = true
	rowHoverEnabled: boolean = this.hover
	private renderDataDetailsModal: boolean = false

	private CalculationType = CalculationType;
	private locationLabels = LocationLabels

	formatFieldsMixin = formatFieldsMixin
	isBuiltInCaseColumn = isBuiltInCaseColumn

	private getHrefForExternalLink: (link: string | null) => string | null = getHrefForExternalLink

	/**
	 * Built-in columns that require separate handling for inline editing
	 */
	private inlineEditSpecialColumnIds: string[] = ["statusId", "severityId", "areas", "tags"]

	get columnValues (): { [key: string]: any }[] {
		const hook = this.reactivityHook
		return this.cases.map(c => {
			return this.columns.reduce((values: { [key: string]: any }, column) => {
				if (c) {
					values[column.key] = cellValue(column, c)
				}
				return values
			}, { case: c })
		})
	}

	get sortedValues (): { [key: string]: any }[] {
		return this.sortColumn
			? this.columnValues.sortByValue(this.sortColumn, !this.sortAscending)
			: this.columnValues
	}

	/**
	 * Map of column keys that are editable
	 */
	get editableColumns (): { [key: string]: boolean } {
		if (this.disableOldInlineEdit || !this.pageAllowsEdits || this.adHocFiltersApplied || !this.fieldEditingEnabled || (this.getIsFeatureEnabled(TenantFeatures.SelectiveInlineEdit) && this.view?.inlineEditMode === inlineEditMode.None)) {
			return {}
		}

		let editableColumns = this.columns.reduce((values: { [key: string]: boolean }, column) => {
			let inlineEditAvailable = !this.getIsFeatureEnabled(TenantFeatures.SelectiveInlineEdit) || this.view?.inlineEditMode === inlineEditMode.All || this.view?.columnOptions?.[column.key]?.inlineEdit

			if (inlineEditAvailable &&
				(column.dynamicField || (this.getIsFeatureEnabled(TenantFeatures.BuiltInFieldsInlineEdit) && !column.isReadonly)) &&
				editableFieldTypes.has(column.dataType) && !column.key?.startsWith("calculation.")
			) {
				values[column.key] = true
			}
			return values
		}, {})

		return editableColumns
	}

	/**
	 * Map of column keys to enabled rule options
	 */
	get columnStyleRules (): { [key: string]: { icon: boolean, background: boolean, bar: boolean } } {
		if (!this.undynamicFormattingRules) return {}

		// Create new object with same keys as undynamicFormattingRules, but contain bools for if each column key has rules
		return Object.entries(this.undynamicFormattingRules).reduce((values:
		{ [key: string]: {
			icon: boolean,
			background: boolean,
			bar: boolean
		}},
		[key, value]) => {
			if (value && value.length > 0) {
				values[key] = {
					icon: value.some((rule: FormattingRule) => !!rule.icon),
					background: value.some((rule: FormattingRule) => !!rule.setBackgroundColor),
					bar: value.some((rule: FormattingRule) => !!rule.setBar)
				}
			}
			return values
		}, {})
	}

	get adHocFiltersApplied (): boolean {
		return (this.checkStyleFilterSelections && Object.keys(this.checkStyleFilterSelections).length > 0) ||
		(this.filterFunctions && this.filterFunctions.length > 0)
	}

	get currentPageItems (): { [key: string]: any }[] {
		if (!this.perPage || this.currentPage === null) {
			return this.sortedValues
		}

		return this.sortedValues.slice((this.currentPage - 1) * this.perPage, this.currentPage * this.perPage)
	}

	get undynamicFormattingRules (): { [key: string]: FormattingRule[] } {
		if (!this.formattingRules) {
			return {}
		}

		const cloneRules = cloneDeep(this.formattingRules)
		Object.keys(cloneRules).forEach((key: string) => {
			cloneRules[key].forEach(rule => {
				if (rule.dynamicValue) {
					const column = this.columns.find(c => c.key === key)
					if (column) {
						if (column.fieldDataType === DataType.dateTime) {
							const now = new Date()
							now.setDate(now.getDate() + parseInt(rule.value))
							rule.value = now
						} else if (column.fieldDataType === DataType.userRole) {
							rule.value = this.me.id
						}
					}
				}
			})
		})
		return cloneRules
	}

	get myTeams (): Set<string> | null {
		if (!this.me || !this.me.teams) return null
		return new Set<string>(this.me.teams.map(t => t.id))
	}

	get sourceTypeEnabled (): boolean {
		return this.tenantEnabledFeatures[TenantFeatures.SourceType]
	}

	get isEditingBuiltInField () {
		return isBuiltInCaseColumn(this.columnKeyToEdit)
	}

	get valueToEdit (): any {
		if (this.disableOldInlineEdit || !this.caseToEdit || !this.columnKeyToEdit) {
			return null
		}

		return this.isEditingBuiltInField
			? this.caseToEdit[this.columnKeyToEdit]
			: (this.fieldToEdit && this.caseToEdit.fields[this.fieldToEdit.field.id!].value) ?? null
	}

	set valueToEdit (value: any) {
		if (!this.caseToEdit || !this.columnKeyToEdit) {
			return
		}

		if (this.isEditingBuiltInField) {
			this.caseToEdit[this.columnKeyToEdit] = value
		} else {
			this.caseToEdit.fields[this.fieldToEdit!.field.id!].value = value
		}
	}

	private mounted () {
		this.showTimeAsHoursMinutes = this.getIsFeatureEnabled(TenantFeatures.UserRoleTimeAsHoursMinutes)
	}

	public async rowClick (caseitem: any, ctrlClick: boolean = false) {
		// newTab is default
		var viewsOpening = ViewsOpeningOption.NewTab
		if (this.userSettings && this.userSettings.settings && this.userSettings.settings.viewsOpening) {
			viewsOpening = this.userSettings.settings.viewsOpening
		}

		let entityIdentifier = caseitem.caseNumber
		let endPath = "cases"

		if (this.displayingUsers) {
			entityIdentifier = caseitem.id
			endPath = "userprofile"
		}

		const encodedEntityIdentifier = encodeURIComponent(entityIdentifier)

		if (this.editModeEnabled) {
			if (ctrlClick) {
				switch (viewsOpening) {
					case ViewsOpeningOption.SameTab:
						this.$router.push({ path: `/${endPath}/${encodedEntityIdentifier}` })
						break
					case ViewsOpeningOption.NewTab:
						let routeData = this.$router.resolve({ path: `/${endPath}/${encodedEntityIdentifier}` })
						window.open(routeData.href, "_blank")
						break
				}
				return
			}

			if (caseitem.id !== this.caseIdToEdit) {
				const token = ++this.rowClickRequestToken
				try {
					this.$emit("loading-case", true)
					this.$emit("sorted-values-updated", this.sortedValues, this.sortColumn, this.sortAscending, true)
					const caseToEdit = await api.caseDetails(caseitem.caseNumber)

					if (token !== this.rowClickRequestToken) return

					this.$emit("setCaseToEdit", caseToEdit)
				} catch (err) {
					if (token !== this.rowClickRequestToken) return
					this.$bvToast.toast("Could not load case details for inline edit", {
						title: "Error",
						variant: "danger",
						solid: true
					})
					this.$emit("setCaseToEdit", null)
				} finally {
					// Only clear the loading state if this is the latest request
					if (token === this.rowClickRequestToken) {
						this.$emit("loading-case", false)
					}
				}
			}
			return
		}

		if (this.showSelectColumn) {
			return
		}

		if (!ctrlClick && this.disableRowClick) {
			return
		}

		if (!caseitem) {
			return
		} else if (caseitem.caseNumber === "FORECAST") {
			this.$emit("forecastClicked", caseitem)
			return
		}

		// Navigate to record in the same tab or in a new tab
		switch (viewsOpening) {
			case ViewsOpeningOption.SameTab:
				if (ctrlClick) {
					let routeData = this.$router.resolve({ path: `/${endPath}/${encodedEntityIdentifier}` })
					window.open(routeData.href, "_blank")
				} else {
					this.$router.push({ path: `/${endPath}/${encodedEntityIdentifier}` })
				}
				break
			case ViewsOpeningOption.NewTab:
				if (ctrlClick) {
					this.$router.push({ path: `/${endPath}/${encodedEntityIdentifier}` })
				} else {
					let routeData = this.$router.resolve({ path: `/${endPath}/${encodedEntityIdentifier}` })
					window.open(routeData.href, "_blank")
				}
				break
		}
	}

	public itemValue (caseItem: any, columnKey: string): any {
		if (!caseItem) {
			return null
		}

		const dotIndex = columnKey.indexOf(".")
		if (dotIndex > -1) {
			const topLevel = columnKey.substring(0, dotIndex)
			const rest = columnKey.substring(dotIndex + 1)
			return this.itemValue(caseItem[topLevel], rest)
		}
		var value = caseItem[columnKey]

		// If the items a calulated field
		if (caseItem.dataType === "calculated" && typeof value === "object") {
			// Add the field's id to the value object
			value.id = caseItem.id
		}

		return value
	}

	public calculatedFieldDisplayValue (field: any, caseValue: any): string | null {
		if (!field) {
			return null
		}

		const value = this.itemValue(caseValue, field.key)
		const fieldExtensions = field.extensions ? field.extensions : field.fieldExtensions

		if (!fieldExtensions || !value) {
			return null
		}
		return calculatedValueDisplay.methods.displayCalculatedField(caseValue, fieldExtensions, value.value, false,
			this.$store)
	}

	private isTimeSpentCalculationType (field: any) : boolean {
		const fieldExtensions = field.extensions ? field.extensions : field.fieldExtensions
		return fieldExtensions && fieldExtensions.calculationType === CalculationType.TimeSpentAs
	}

	private cellStyle (key: string, value: any, field: CaseColumn) {
		if (!this.undynamicFormattingRules || !this.undynamicFormattingRules[key]) return null
		return cellFormatting.cellStyle(this.undynamicFormattingRules[key], value, field)
	}

	private cellBarStyle (caseDetails: { [key: string]: any }, key: string, value: any, field: CaseColumn) {
		if (!this.undynamicFormattingRules || !this.undynamicFormattingRules[key]) {
			return {
				display: "none"
			}
		}

		return cellFormatting.cellBarStyle(this.undynamicFormattingRules[key], this.currentPageItems, caseDetails, value, field)
	}

	private icon (key: string, value: any, field: CaseColumn) {
		if (!this.undynamicFormattingRules || !this.undynamicFormattingRules[key]) return null
		return cellFormatting.icon(this.undynamicFormattingRules[key], value, field)
	}

	private setSortColumn (columnKey: string) {
		if (this.sortColumn === columnKey) {
			this.sortAscending = !this.sortAscending
		} else {
			this.sortColumn = columnKey
			this.sortAscending = true
		}
	}

	// Edit Table Cell functionality

	disableRowClick: boolean = false

	columnKeyHovered: string = "";
	caseIdHovered: string = ""
	columnKeyToEdit: string = "";
	editFieldDataType: FieldTypeId | null = null
	editCaseTemplate: TemplateDetails | null = null

	caseToEdit: CaseDetails | null = null
	fieldToEdit: CaseField | null = null

	private inlineEditedCaseIds: string[] = []

	bulkDelete: string [] = [];

	public get fieldEditingEnabled (): boolean {
		return !!(this.getTenantSettings?.[TenantSettingKeys.AllowFieldEditingInViews])
	}

	private cellIsEditable (caseHovered: CaseDetails, column: CaseColumn) : boolean {
		if (caseHovered.isClosed) {
			return false
		}

		// Prevents editing of Forecasted Records
		if (!caseHovered.id) {
			return false
		}

		const fieldHovered = caseHovered.fields[column.fieldId!]
		return fieldHovered && this.hasPermission(caseHovered, fieldHovered)
	}

	// This checks for field level and case field level permissions and hides pencil icon if not authorized - it's not possible to do anything with *section permissions* for Views as these don't get returned by API (in this case).
	// Even if an edit is made by a user without permission, the API also checks permissions at api.selectCaseField(..), and disallows it with a NoPermissionException if not authorized
	private hasPermission (caseHovered: CaseDetails, fieldHovered: CaseField): boolean {
		if (fieldHovered.field && fieldHovered.field.displayReadOnly) {
			return false
		}

		const teams = this.getPermissionTeams("edit", caseHovered, fieldHovered)
		if (!teams || teams.length === 0) {
			return true
		}

		return !!this.myTeams && teams.some(team => this.myTeams!.has(team.id))
	}

	private getPermissionTeams (permissionType: keyof Permission, caseHovered: CaseDetails, fieldHovered: CaseField): any[] {
		if (!caseHovered || !fieldHovered) {
			return []
		}

		var caseFieldLevelPermissions = fieldHovered.permissions
			? caseHovered.isCase ? fieldHovered.permissions.case : fieldHovered.permissions.record
			: null

		if (caseFieldLevelPermissions) {
			return caseFieldLevelPermissions[permissionType]
		}

		if (fieldHovered.field && fieldHovered.field.permissions) {
			const fieldLevelPermissions =
				(caseHovered.isCase ? fieldHovered.field.permissions.case : fieldHovered.field.permissions.record)[permissionType]
			return fieldLevelPermissions
		}

		return []
	}

	private async cellEditBegin (caseNumber: string, caseId: string, columnKey: string) {
		const caseIdToEdit = caseId
		Vue.set(this, "columnKeyToEdit", columnKey)

		const fieldToEditId = this.stripFieldsPrefix(this.columnKeyToEdit)

		try {
			await api.selectCaseField(caseIdToEdit, fieldToEditId, true)
		} catch (e) {
			this.$bvToast.toast("Check the console for details", {
				title: "Cannot Edit Field",
				variant: "danger",
				solid: true
			})
			await this.cellEditClear()
			return
		}

		this.caseIdToEdit = caseIdToEdit
		this.caseToEdit = await api.caseDetails(caseNumber)

		this.editFieldDataType = (this.isEditingBuiltInField
			? getBuiltInCaseColumn(this.columnKeyToEdit)!.dataType
			: this.caseToEdit.fields[fieldToEditId].field.dataType) as FieldTypeId

		this.fieldToEdit = !this.isEditingBuiltInField
			? this.caseToEdit.fields[fieldToEditId]
			: null
		this.disableRowClick = true
		this.rowHoverEnabled = false

		await this.$nextTick()
		if (this.getIsFeatureEnabled(TenantFeatures.InlineEditCenterEditingRow)) {
			this.scrollCaseRowIntoView(caseId)
		}
	}

	private stripFieldsPrefix (fieldId: string) : string {
		if (fieldId.startsWith("fields.")) {
			return fieldId.slice("fields.".length)
		}
		return fieldId
	}

	private async cellEditClear (changeMade: boolean = false) {
		const caseId = this.caseIdToEdit

		const caseFieldId = this.isEditingBuiltInField ? this.columnKeyToEdit : this.fieldToEdit!.field.id!
		if (caseId && caseFieldId) {
			await api.selectCaseField(caseId, caseFieldId, false)
		}
		this.caseIdToEdit = ""
		this.columnKeyToEdit = ""
		this.editFieldDataType = null
		this.caseToEdit = null
		this.fieldToEdit = null
		this.disableRowClick = false
		this.rowHoverEnabled = true
		this.editCaseTemplate = null

		await this.$nextTick()
		if (this.getIsFeatureEnabled(TenantFeatures.InlineEditCenterEditingRow)) {
			if (caseId) {
				this.scrollCaseRowIntoView(caseId)
			}
		}
	}

	private async cellEditConfirmed (caseNumber: string) {
		const caseFieldId = this.isEditingBuiltInField ? this.columnKeyToEdit : this.fieldToEdit!.field.id!
		let saveCase = false

		// perform validation / update logic for fields that require it
		switch (caseFieldId) {
			case "cadenceEnabled":
			{
				normalizeCaseCadenceProperties(this.caseToEdit!, this.editCaseTemplate!, this.getIsFeatureEnabled)
				saveCase = true
				break
			}
		}

		if (saveCase) {
			if (this.caseToEdit) {
				// discard the result of this, as the rule runner is not given a chance to run before it's returned
				await api.saveCase(this.caseToEdit)
			}
		} else if (this.isEditingBuiltInField) {
			await api.saveCaseProperty(this.caseToEdit!.id!, caseFieldId, this.caseToEdit![caseFieldId])
		} else {
			await api.saveCaseField(this.caseToEdit!.id!, caseFieldId, this.fieldToEdit!.value)
		}

		const updatedCase = await api.caseDetails(caseNumber)
		const caseIndex = this.cases.findIndex(c => c.caseNumber === caseNumber)
		this.$set(this.cases, caseIndex, updatedCase)
		this.reactivityHook++ // Force reactivity for inline edit
		await this.cellEditClear(true)
		if (updatedCase && !this.inlineEditedCaseIds.includes(updatedCase.id!)) {
			this.inlineEditedCaseIds.push(updatedCase.id!)
		}

		this.$emit("caseEdited", caseNumber)
	}

	private cellIsHovered (caseIdHovered: string, columnKeyHovered: string) : boolean {
		return caseIdHovered === this.caseIdHovered && columnKeyHovered === this.columnKeyHovered
	}

	private setHoveringCell (caseIdHovered: string, columnKeyHovered: string) {
		if (this.editableColumns[columnKeyHovered]) {
			this.columnKeyHovered = columnKeyHovered
			this.caseIdHovered = caseIdHovered
		}
	}

	private clearHoveringCell () {
		this.columnKeyHovered = ""
		this.caseIdHovered = ""
	}

	private cellIsInEditMode (caseIdToEdit: string, columnKeyToEdit: string) : boolean {
		if (!this.fieldEditingEnabled) {
			return false
		}

		return this.caseIdToEdit === caseIdToEdit &&
		this.columnKeyToEdit === columnKeyToEdit &&
		(this.isEditingBuiltInField || !!this.fieldToEdit)
	}

	public get pageAllowsEdits () {
		return this.currentRouteName === "dashboard" ||
		this.currentRouteName === "overview" || // views
		this.currentRouteName === "v2-cases-overview" || // views in new interface
		this.currentRouteName === "insights"
	}

	public get hasDefaultSlot () {
		return !!this.$slots["default"]
	}

	public get hasFooterSlot () {
		return !!this.$slots["footer"]
	}

	public updateBulkDelete (caseItem: string | any []) {
		if (this.checkedItems.length > this.bulkDelete.length) {
			this.bulkDelete = this.checkedItems
		}

		if (typeof caseItem === "string") {
			this.bulkDelete.includes(caseItem) ? this.bulkDelete.remove(caseItem) : this.bulkDelete.push(caseItem)
		} else {
			this.bulkDelete = caseItem.length === this.bulkDelete.length ? [] : caseItem.map(c => c.case.id)
		}

		this.$emit("getBulkDelete", this.bulkDelete)
	}

	private get checkedStates () {
		if (!this.currentPageItems || !this.showSelectColumn) return []

		// Convert checkedItems to a Set for efficient lookup
		const checkedItemsSet = new Set(this.checkedItems)

		return this.currentPageItems.map(caseItem => checkedItemsSet.has(caseItem.case.id))
	}

	private isBuiltInFieldEditableOnCase (record: CaseDetails, column: CaseColumn): boolean {
		const columnDefinition = getBuiltInCaseColumn(column.key) as CaseColumn

		if (!columnDefinition) {
			throw new Error(`Column ${column.key} is not a built-in field`)
		}

		if (columnDefinition.isReadonly) return false

		if (columnDefinition.adminOnly && !this.me.isAdmin) return false

		var editable = true
		switch (column.key) {
			case "pendingTriage":
				editable = false
				break
			case "title":
				editable = !record.titleTemplateFormat
				break
			case "cadenceEnabled":
				editable = !record.closed && this.casesWithUserEditableCadence.has(record.id!)
				break
		}

		return editable
	}

	@Watch("updatedCaseKey", { immediate: true })
	private async onUpdatedCaseKeyChange (updatedCaseKey: number) {
		if (!this.updatedCase) return

		const caseIndex = this.cases.findIndex(c => c.caseNumber === this.updatedCase?.caseNumber)
		if (caseIndex !== -1) {
			const updatedCaseData = cloneDeep(this.updatedCase)
			// Preserve existing calculation column fields
			if (this.cases[caseIndex].fields) {
				Object.keys(this.cases[caseIndex].fields).forEach(key => {
					if (key.startsWith("calculation.")) {
						updatedCaseData.fields[key] = this.cases[caseIndex].fields[key]
					}
				})
			}
			this.$set(this.cases, caseIndex, updatedCaseData)
			this.reactivityHook++ // Force reactivity for inline edit
			this.$emit("updated-case-consumed", true)
		}
	}

	@Watch("editModeEnabled")
	private async onEditModeChange (newValue: boolean) {
		if (!newValue) {
			this.$emit("setCaseToEdit", null)
		} else if (this.isFirstGroup) {
			const firstCaseItem = this.currentPageItems[0].case
			try {
				this.$emit("loading-case", true)
				const caseToEdit = await api.caseDetails(firstCaseItem.caseNumber)
				this.$emit("setCaseToEdit", caseToEdit)
			} catch (err) {
				this.$bvToast.toast("Could not load case details for inline edit", {
					title: "Error",
					variant: "danger",
					solid: true
				})
				this.$emit("setCaseToEdit", null)
			} finally {
				this.$emit("sorted-values-updated", this.sortedValues, this.sortColumn, this.sortAscending)
				this.$emit("loading-case", false)
			}
		}
	}

	@Watch("sortedValues", { immediate: true })
	onSortedValuesChange (newValues: { [key: string]: any }[]) {
		this.$emit("sorted-values-updated", newValues, this.sortColumn, this.sortAscending)
	}

	@Watch("caseIdToEdit")
	onCaseIdToEditChange (newCaseId: string) {
		if (this.disableOldInlineEdit && this.getIsFeatureEnabled(TenantFeatures.InlineEditCenterEditingRow)) {
			this.scrollCaseRowIntoView(newCaseId)
		}
	}

	@Watch("showSelectColumn")
	public resetBulkDelete (newValue: boolean) {
		if (!newValue && !!this.bulkDelete && !!this.bulkDelete.length) {
			this.bulkDelete = []
		}
	}

	get casesWithUserEditableCadence (): Set<string> {
		if (this.disableOldInlineEdit ||
			!this.columns.some(c => c.key === "cadenceEnabled") ||
			this.view?.inlineEditMode === inlineEditMode.None ||
			(this.view?.inlineEditMode === inlineEditMode.Some && !this.view?.columnOptions?.cadenceEnabled?.inlineEdit)
		) {
			return new Set<string>()
		}

		const templates = this.templateCache.items
		const currentPageCaseIds = new Set<string>(this.currentPageItems?.map(item => item["case"].id as string) ?? [])

		return new Set<string>(this.cases.filter(record => currentPageCaseIds.has(record.id!)).filter(record => {
			const template = templates.find(t => t.id === record.templateId)
			return template && getCanUserDisableCadence(record, this.me, template, this.getIsFeatureEnabled)
		}).map(record => record.id!))
	}

	get templatesEditableAfterClose (): Set<string> {
		if (this.disableOldInlineEdit || !this.view || this.view.inlineEditMode === inlineEditMode.None) {
			return new Set<string>()
		}

		const templates = this.templateCache.items

		return new Set<string>(templates.filter(template => {
			return template.editClosedPermissions?.some(teamId => this.userTeamsIds.has(teamId))
		}).map(record => record.id!))
	}

	private inlineEditMinWidthByDataType (fieldType: FieldTypeId | null): string {
		switch (fieldType) {
			case FieldTypeId.ExternalLink:
			case FieldTypeId.ListMultiple:
			case FieldTypeId.ListData:
			case FieldTypeId.UserRole:
			case FieldTypeId.Text:
			case FieldTypeId.TextArea:
				return "15vw"
			case FieldTypeId.List:
				return "10vw"
			case FieldTypeId.Number:
				return "6vw"
			default:
				return "5vw"
		}
	}

	private async addDataObjectItem (caseField: CaseField, newValue: any): Promise<void> {
		return DataObjectMethods.addItem(caseField, newValue, this.caseToEdit!, true)
	}

	private async updateDataObjectItem (caseField: CaseField, updatedValue: any): Promise<void> {
		return DataObjectMethods.updateItem(caseField, updatedValue, this.caseToEdit!, true)
	}

	private async removeDataObjectItem (caseField: CaseField, newValue: any): Promise<void> {
		return DataObjectMethods.removeItem(caseField, newValue, this.caseToEdit!, true)
	}

	private scrollCaseRowIntoView (caseId: string) {
		const caseRow = this.$el.querySelector(`#caseRow-${caseId}`) as HTMLElement
		if (!caseRow) return
		caseRow.scrollIntoView({
			behavior: "auto",
			block: "center"
		})
	}

	private get userTeamsIds (): Set<string> {
		if (!this.me || !this.me.teams) {
			return new Set()
		}
		return new Set(this.me.teams.map(t => t.id))
	}

	private async openDataDetailsModal (details: any) {
		this.renderDataDetailsModal = true
		await this.$nextTick()
		this.dataDetailsModal.open(details)
	}

	private configureCheckStyleFilter (column: CaseColumn) {
		this.$emit("configureCheckStyleFilter", column)
	}

	private shouldShowReRunInsight (columnKey: string, caseItem: { [key: string]: any }): boolean {
		const caseId = caseItem.case.id
		return (
			caseId in this.cellErrors &&
			this.cellErrors[caseId].columnKeys.includes(columnKey)
		)
	}
}

